import {
  generateBlockWithAi,
  mapGenerateExamError,
  type EvaluationBlock,
  type EvaluationExam,
  type GameMode,
} from '@interactive-room';
import { buildGuideContextFromBlocks, supportsBlockAi } from '../constants/blockAiParams';
import type { ExamLevelId } from '../constants/examAiParams';
import { resolveExamAiLevel, resolveExamAiSubjectTag } from '../lib/resolveExamAiIdentity';
import { resolvePrintBlockRole } from './printBlockRole';

const MAX_BLOCKS_PER_TURN = 4;

export type PrintChatTarget =
  | { kind: 'indices'; indices: number[] }
  | { kind: 'all_supported' };

/** Intención semántica del pedido (para no meter ejercicios cuando piden contexto). */
export type PrintChatSemanticKind =
  | 'teaching' // contexto, teoría, conceptos, explicar
  | 'practice' // ejercicios, preguntas, práctica
  | 'style'; // tono, acortar, simplificar (mantiene rol del bloque)

export type PrintChatPlan = {
  target: PrintChatTarget;
  semantic: PrintChatSemanticKind;
  /** Si no hay bloque teaching y el pedido es de contexto, crear/reescribir como info_block. */
  ensureTeachingBlock: boolean;
};

/** Extrae referencias a bloques (“bloque 3”, “#2”, “bloques 1 y 4”). */
export function parsePrintChatTargets(message: string, blockCount: number): PrintChatTarget {
  const lower = message.toLowerCase();
  if (/\b(todos|todas|entera|completo|completa)\b/.test(lower)) {
    return { kind: 'all_supported' };
  }

  const found = new Set<number>();
  const patterns = [
    /\bbloques?\s*#?\s*(\d{1,2})\b/gi,
    /#\s*(\d{1,2})\b/g,
    /\bn[úu]mero\s*(\d{1,2})\b/gi,
  ];
  for (const pattern of patterns) {
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(message)) !== null) {
      const n = Number(match[1]);
      if (Number.isFinite(n) && n >= 1 && n <= blockCount) found.add(n - 1);
    }
  }

  if (found.size === 0) return { kind: 'all_supported' };
  return { kind: 'indices', indices: [...found].sort((a, b) => a - b) };
}

export function detectPrintChatSemanticKind(message: string): PrintChatSemanticKind {
  const lower = message.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');

  const teachingHints =
    /\b(contexto|contextualiz|teor|concepto|explic|introduc|marco|fondo|leer|lectura|informativ|glosario|definicion|definir|contenido\s+teoric)/.test(
      lower,
    );
  const practiceHints =
    /\b(ejercicio|practica|pregunta|preguntas|actividad|desafio|completar|evalua|ensayo|resolver|problemas)/.test(
      lower,
    );
  const styleHints =
    /\b(simple|simplifica|tono|acorta|acortar|mas\s+corto|claro|cercano|adapta|nivel|dificultad|reescribe|mejora\s+el\s+lenguaje)/.test(
      lower,
    );

  // Pedido de “agregar/poner contexto” gana sobre práctica.
  if (teachingHints && !practiceHints) return 'teaching';
  if (practiceHints && !teachingHints) return 'practice';
  if (teachingHints && practiceHints) {
    // “contexto y luego ejercicios” → priorizar teaching en este turno
    if (/\b(primero|antes|agrega(r)?\s+(un\s+)?contexto|poner\s+contexto)\b/.test(lower)) {
      return 'teaching';
    }
    return 'style';
  }
  if (styleHints) return 'style';
  // Por defecto: si dice “agregar X” temático sin “ejercicio”, tratar como teaching
  if (/\b(agrega|añade|agregar|anadir|incluye|incluir|pon(er)?)\b/.test(lower) && !practiceHints) {
    return 'teaching';
  }
  return 'style';
}

export function planPrintChatRevision(message: string, blockCount: number): PrintChatPlan {
  const target = parsePrintChatTargets(message, blockCount);
  const semantic = detectPrintChatSemanticKind(message);
  const ensureTeachingBlock =
    semantic === 'teaching' &&
    (/\b(agrega|añade|agregar|anadir|incluye|incluir|pon(er)?|falta)\b/.test(
      message.toLowerCase().normalize('NFD').replace(/\p{M}/gu, ''),
    ) ||
      /\bcontexto\b/.test(message.toLowerCase()));
  return { target, semantic, ensureTeachingBlock };
}

function summarizeBlock(block: EvaluationBlock, index: number): string {
  const payload = (block.payload ?? {}) as Record<string, unknown>;
  const role = resolvePrintBlockRole(block.mode);
  const title = String(block.label || payload.title || payload.sectionTitle || `Bloque ${index + 1}`);
  const snippet = String(
    payload.bodyText || payload.text || payload.prompt || payload.instruction || '',
  )
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 180);
  return `#${index + 1} [${block.mode}/${role}] ${title}${snippet ? ` — ${snippet}` : ''}`;
}

function buildBlockIntent(
  message: string,
  exam: EvaluationExam,
  blockIndex: number,
  semantic: PrintChatSemanticKind,
  forcedMode?: GameMode,
): string {
  const outline = exam.blocks.map((b, i) => summarizeBlock(b, i)).join('\n');
  const current = summarizeBlock(exam.blocks[blockIndex]!, blockIndex);
  const mode = forcedMode || exam.blocks[blockIndex]!.mode;

  const roleRules =
    semantic === 'teaching'
      ? [
          'ROL OBLIGATORIO: bloque INFORMÁTIVO / de CONTEXTO o CONCEPTOS (enseñanza).',
          'NO generes ejercicios, opciones múltiples, huecos ni consignas de práctica.',
          'Entregá prosa clara, ejemplos y vocabulario del tema pedido. Si el modo es info_block, usá bodyText/sectionTitle.',
          'Si el docente pidió “agregar contexto”, profundizá o reescribí el marco teórico; no agregues preguntas.',
        ]
      : semantic === 'practice'
        ? [
            'ROL OBLIGATORIO: bloque de PRÁCTICA / ejercicios.',
            'Generá consignas o ítems accionables alineados al tema; no reemplaces por un texto solo teórico.',
          ]
        : [
            'Mantené el mismo rol pedagógico del bloque (teaching vs practice).',
            'Si es teaching, no lo conviertas en ejercicios. Si es practice, no lo conviertas en monólogo teórico.',
          ];

  return [
    'Sos el copiloto de edición de una guía/evaluación ya existente (WYSIWYG).',
    `Reescribí SOLO este bloque. Modo objetivo: ${mode}.`,
    ...roleRules,
    `Material: «${exam.title}».`,
    `Pedido del docente: ${message.trim()}`,
    `Bloque a regenerar: ${current}`,
    'Mapa del documento:',
    outline,
  ].join('\n');
}

function indicesByRole(
  exam: EvaluationExam,
  supported: number[],
  role: 'teaching' | 'practice',
): number[] {
  return supported.filter((i) => resolvePrintBlockRole(exam.blocks[i]!.mode) === role);
}

export interface ReviseExamFromChatArgs {
  exam: EvaluationExam;
  message: string;
  subjectExpertPrompt?: string;
  level?: ExamLevelId | string;
  onProgress?: (done: number, total: number, blockIndex: number) => void;
}

export interface ReviseExamFromChatResult {
  exam: EvaluationExam;
  updatedIndices: number[];
  skipped: number;
  assistantSummary: string;
}

export async function reviseExamFromChat(
  args: ReviseExamFromChatArgs,
): Promise<ReviseExamFromChatResult> {
  const { exam, message, subjectExpertPrompt, level, onProgress } = args;
  const resourceType = exam.resourceType === 'guide' ? 'guide' : 'exam';
  const plan = planPrintChatRevision(message, exam.blocks.length);

  const supportedIndices = exam.blocks
    .map((block, index) => ({ block, index }))
    .filter(({ block }) => supportsBlockAi(block.mode, resourceType, exam.subjectTag))
    .map(({ index }) => index);

  let indices: number[] = [];
  let skippedUnsupported = 0;
  let forcedModeByIndex = new Map<number, GameMode>();

  if (plan.target.kind === 'indices') {
    indices = plan.target.indices.filter((i) => supportedIndices.includes(i));
    skippedUnsupported = plan.target.indices.length - indices.length;
    // Si pidieron teaching y eligieron un índice practice, aún reescribimos ese índice
    // pero forzamos info_block cuando ensureTeachingBlock.
    if (plan.semantic === 'teaching' && plan.ensureTeachingBlock) {
      for (const i of indices) {
        if (resolvePrintBlockRole(exam.blocks[i]!.mode) !== 'teaching') {
          forcedModeByIndex.set(i, 'info_block');
        }
      }
    }
  } else if (plan.semantic === 'teaching') {
    indices = indicesByRole(exam, supportedIndices, 'teaching');
    if (indices.length === 0 && plan.ensureTeachingBlock) {
      // No hay bloque de contexto: usar el primero y convertirlo a info_block.
      const fallback = supportedIndices[0] ?? 0;
      indices = [fallback];
      forcedModeByIndex.set(fallback, 'info_block');
    }
  } else if (plan.semantic === 'practice') {
    indices = indicesByRole(exam, supportedIndices, 'practice');
    if (indices.length === 0) indices = supportedIndices;
  } else {
    indices = supportedIndices;
  }

  if (indices.length === 0) {
    throw new Error(
      plan.semantic === 'teaching'
        ? 'No encontré un bloque de contexto para editar. Pedí «bloque 1» o abrí el editor para agregar un bloque informativo.'
        : 'No hay bloques compatibles con IA para ese pedido. Probá «bloque 2» o «haz el tono más simple».',
    );
  }

  let truncated = false;
  // Pedidos de contexto: preferir 1–2 bloques teaching, no barrer toda la guía.
  const cap =
    plan.semantic === 'teaching' && plan.target.kind === 'all_supported'
      ? Math.min(2, MAX_BLOCKS_PER_TURN)
      : MAX_BLOCKS_PER_TURN;
  if (indices.length > cap) {
    indices = indices.slice(0, cap);
    truncated = true;
  }

  let nextExam = exam;
  const updatedIndices: number[] = [];
  const resolvedLevel = resolveExamAiLevel(exam, level);
  const resolvedSubject = resolveExamAiSubjectTag(exam);
  if (!resolvedLevel) {
    throw new Error(
      'level requerido::Falta el nivel curricular. Elegilo en el chat o abrí el material desde un curso con nivel.',
    );
  }
  if (!resolvedSubject) {
    throw new Error(
      'subjectTag requerido::Este material no tiene asignatura. Regeneralo desde el curso o la asignatura.',
    );
  }

  for (let step = 0; step < indices.length; step += 1) {
    const blockIndex = indices[step]!;
    const block = nextExam.blocks[blockIndex]!;
    const forcedMode = forcedModeByIndex.get(blockIndex);
    const mode = (forcedMode || block.mode) as GameMode;
    onProgress?.(step, indices.length, blockIndex);

    try {
      const result = await generateBlockWithAi({
        mode,
        userIntent: buildBlockIntent(message, nextExam, blockIndex, plan.semantic, forcedMode),
        examTitle: nextExam.title,
        subjectTag: resolvedSubject,
        subjectExpertPrompt,
        level: resolvedLevel,
        resourceType,
        blockIndex,
        currentLabel: block.label,
        guideContext:
          resourceType === 'guide'
            ? buildGuideContextFromBlocks(nextExam.blocks, blockIndex)
            : undefined,
      });

      const nextBlock: EvaluationBlock = {
        ...block,
        mode,
        label: result.label || block.label,
        payload: result.payload as EvaluationBlock['payload'],
      };
      nextExam = {
        ...nextExam,
        blocks: nextExam.blocks.map((b, i) => (i === blockIndex ? nextBlock : b)),
      };
      updatedIndices.push(blockIndex);
    } catch (err) {
      const code = err instanceof Error ? err.message : 'generate_block_failed';
      throw new Error(mapGenerateExamError(code));
    }

    onProgress?.(step + 1, indices.length, blockIndex);
  }

  const labels = updatedIndices.map((i) => `#${i + 1}`).join(', ');
  const roleLabel =
    plan.semantic === 'teaching'
      ? 'contexto/teoría'
      : plan.semantic === 'practice'
        ? 'práctica'
        : 'estilo';
  const parts = [
    updatedIndices.length === 1
      ? `Actualicé el bloque ${labels} (${roleLabel}) según tu pedido.`
      : `Actualicé los bloques ${labels} (${roleLabel}) según tu pedido.`,
  ];
  if (forcedModeByIndex.size > 0) {
    parts.push('Convertí al menos un bloque a informativo para cubrir el contexto pedido.');
  }
  if (truncated) {
    parts.push(`Limitado a ${cap} bloques por turno; pedí de nuevo para seguir.`);
  }
  if (skippedUnsupported > 0) {
    parts.push(`${skippedUnsupported} bloque(s) no admiten regeneración IA.`);
  }

  return {
    exam: nextExam,
    updatedIndices,
    skipped: skippedUnsupported,
    assistantSummary: parts.join(' '),
  };
}
